import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CalendarHeart, Loader2, RotateCcw, Utensils } from "lucide-react";

/** E-C5: put a past order's items back in the cart (same shop when it still
 *  has them, otherwise the cheapest shop that delivers there). */
export function BuyAgainButton({ orderId, size = "sm", className = "", label = "Buy again" }) {
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e?.stopPropagation();
    setBusy(true);
    try {
      const { data } = await api.get(`/customer/orders/${orderId}/buy-again`);
      if (!data.items.length) { toast.error("None of these items are available near you right now."); return; }
      data.items.forEach((i) => addItem({ id: i.product_id, offer_id: i.offer_id, catalog_product_id: i.catalog_product_id, vendor_id: i.vendor_id,
        vendor_name: i.vendor_name, name: i.name, price: i.price, image_url: i.image_url }, i.quantity));
      toast.success(`${data.items.length} item${data.items.length === 1 ? "" : "s"} added to your cart`
        + (data.missing.length ? `. Not available now: ${data.missing.join(", ")}` : ""));
      navigate("/cart");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  return (
    <Button size={size} variant="outline" className={`rounded-full ${className}`} onClick={go} disabled={busy} data-testid={`buy-again-${orderId}`}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RotateCcw className="h-4 w-4 mr-1" />}{label}
    </Button>
  );
}

const dayText = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" });

/** E-C5 + E-C7: Home cards for a signed-in customer: their pets' next
 *  vaccinations, "food may be running low", and the last order to buy again. */
export function ForYouStrip() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => {
    if (user?.role !== "customer") return;
    api.get("/customer/reminders").then(({ data }) => setD(data), () => {});
  }, [user]);
  if (!d || (!d.vaccinations.length && !d.food && !d.last_order)) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-6" data-testid="home-for-you">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {d.vaccinations.map((v) => (
          <Link key={`${v.pet}-${v.vaccine}`} to="/account?tab=pets"
            className={`rounded-2xl border p-4 flex gap-3 items-start hover:shadow-sm ${v.overdue ? "border-terracotta/40 bg-terracotta/5" : "border-teal/30 bg-teal/5"}`} data-testid="home-vaccine-due">
            <CalendarHeart className={`h-5 w-5 shrink-0 ${v.overdue ? "text-terracotta" : "text-teal"}`} />
            <div className="text-sm"><b>{v.pet}'s {v.vaccine}</b> {v.overdue ? "was due" : "is due"} {dayText(v.next_due)}
              <div className="text-xs text-stone-500 mt-0.5">Book it with a vet on PawBazaar Care</div></div>
          </Link>
        ))}
        {d.food && (
          <div className="rounded-2xl border border-marigold/40 bg-marigold/5 p-4 flex gap-3 items-start" data-testid="home-food-low">
            <Utensils className="h-5 w-5 shrink-0 text-marigold" />
            <div className="text-sm flex-1"><b>Food may be running low</b>
              <div className="text-xs text-stone-500 mt-0.5">{d.food.items.join(", ")} · ordered {d.food.days} days ago</div>
              <BuyAgainButton orderId={d.food.order_id} className="mt-2" label="Order again" /></div>
          </div>
        )}
        {d.last_order && (!d.food || d.food.order_id !== d.last_order.id) && (
          <div className="rounded-2xl border border-stone-200 bg-white p-4 flex gap-3 items-start" data-testid="home-last-order">
            <RotateCcw className="h-5 w-5 shrink-0 text-stone-500" />
            <div className="text-sm flex-1"><b>Your last order</b>{d.last_order.vendor_name ? ` from ${d.last_order.vendor_name}` : ""}
              <div className="text-xs text-stone-500 mt-0.5 truncate">{d.last_order.items.join(", ")}{d.last_order.count > 3 ? ` +${d.last_order.count - 3} more` : ""}</div>
              <BuyAgainButton orderId={d.last_order.id} className="mt-2" /></div>
          </div>
        )}
      </div>
    </section>
  );
}
